import {
  assetActions,
  AssetInspectionInfo,
  AssetInspectionRecord,
} from 'actions/asset';
import type { InspectionSelection } from '../assetHubInspectionSelection';

export type InspectionItem = AssetInspectionInfo | AssetInspectionRecord;

export function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isAssetInspectionRecord(
  value: unknown,
): value is AssetInspectionRecord {
  if (!isRecordLike(value)) return false;
  const id = value['id'];
  if (typeof id !== 'number' && typeof id !== 'string') return false;
  return (
    typeof value['inspectionDate'] === 'string' &&
    typeof value['inspectionType'] === 'string' &&
    typeof value['inspector'] === 'string' &&
    typeof value['comments'] === 'string' &&
    (typeof value['signedOff'] === 'boolean' ||
      typeof value['signedOff'] === 'string')
  );
}

function isLikelyInspectionDatesRow(value: unknown): boolean {
  if (!isRecordLike(value)) return false;
  if (isAssetInspectionRecord(value)) return false;
  return typeof value['inspectionType'] === 'string';
}

export function narrowToInspectionItem(
  value: unknown,
): InspectionItem | null {
  if (isAssetInspectionRecord(value)) return value;
  if (isLikelyInspectionDatesRow(value)) {
    return value as AssetInspectionInfo;
  }
  return null;
}

export function getInspectionTypeName(item: unknown): string {
  if (!isRecordLike(item)) return '';
  const raw =
    item['inspectionType'] ?? item['inspection_type'] ?? item['type'];
  if (raw === null || raw === undefined) return '';
  return String(raw).trim();
}

function mapToInspectionItems(values: readonly unknown[]): InspectionItem[] {
  return values
    .map(narrowToInspectionItem)
    .filter((x): x is InspectionItem => x !== null);
}


function getInspectionSortDate(item: InspectionItem): string {
  if (isAssetInspectionRecord(item)) {
    return item.inspectionDate;
  }
  return item.lastInspectionDate ?? '';
}

export async function fetchInspectionItems(
  assetId: number,
  refreshData: boolean,
  preloadedRecords?: readonly unknown[],
): Promise<InspectionItem[]> {
  const actions = assetActions();

  let records: InspectionItem[] = [];
  if (preloadedRecords !== undefined) {
    records = mapToInspectionItems(
      (Array.isArray(preloadedRecords) ? preloadedRecords : []).filter(Boolean),
    );
  } else {
    const recordResult = await actions.getInspectionRecords(
      assetId,
      refreshData,
    );
    const fetchedRaw = Array.isArray(recordResult) ? recordResult : [];
    records = mapToInspectionItems(fetchedRaw.filter(Boolean));
  }

  if (records.length > 0) {
    return records;
  }
  const dateResult = await actions.getAssetInspectionDetails(
    assetId,
    refreshData,
  );
  return mapToInspectionItems(dateResult);
}

export function findLatestInspectionForType(
  items: readonly InspectionItem[],
  displayTitle: string,
): InspectionItem | undefined {
  const normalized = displayTitle.trim().toLowerCase();
  if (!normalized) return undefined;

  let latest: InspectionItem | undefined;
  let latestDate = '';

  for (const item of items) {
    const typeName = getInspectionTypeName(item);
    if (typeName.length === 0 || typeName.toLowerCase() !== normalized) {
      continue;
    }
    const sortDate = getInspectionSortDate(item);
    if (!latest || sortDate >= latestDate) {
      latest = item;
      latestDate = sortDate;
    }
  }

  return latest;
}

export function findInspectionItemByDisplayTitle(
  items: readonly InspectionItem[],
  displayTitle: string,
): InspectionItem | undefined {
  return findLatestInspectionForType(items, displayTitle);
}

export function getInspectionRecordId(
  item: InspectionItem,
): number | string | null {
  if (isAssetInspectionRecord(item)) {
    return item.id;
  }
  return null;
}

const INVALID_INSPECTION_DATE_MARKERS = new Set([
  'n/a',
  'na',
  '?',
  '0000-00-00',
  '9999-99-99',
  'null',
  'undefined',
]);

export function isUsableInspectionDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  return !INVALID_INSPECTION_DATE_MARKERS.has(trimmed.toLowerCase());
}

export interface InspectionSummaryDates {
  readonly [key: string]: string;
  readonly LastInspectionPerformed: string;
  readonly NextInspectionPlanned: string;
}

/*
  Builds the Inspections summary shown in Asset Hub info.
  Prefers schedule last/next dates; falls back to the latest record date for last performed.
 */
export function resolveInspectionSummaryDates(
  scheduleRows: readonly {
    readonly lastInspectionDate?: string;
    readonly nextInspectionDate?: string;
  }[],
  records: readonly { readonly inspectionDate?: string }[] = [],
): InspectionSummaryDates {
  let lastFromSchedule = '';
  let nextFromSchedule = '';

  for (const row of scheduleRows) {
    if (isUsableInspectionDate(row.lastInspectionDate)) {
      if (!lastFromSchedule || row.lastInspectionDate > lastFromSchedule) {
        lastFromSchedule = row.lastInspectionDate;
      }
    }
    if (isUsableInspectionDate(row.nextInspectionDate)) {
      if (!nextFromSchedule || row.nextInspectionDate < nextFromSchedule) {
        nextFromSchedule = row.nextInspectionDate;
      }
    }
  }

  let lastFromRecords = '';
  for (const record of records) {
    if (isUsableInspectionDate(record.inspectionDate)) {
      if (!lastFromRecords || record.inspectionDate > lastFromRecords) {
        lastFromRecords = record.inspectionDate;
      }
    }
  }

  return {
    LastInspectionPerformed: lastFromSchedule || lastFromRecords || 'N/A',
    NextInspectionPlanned: nextFromSchedule || 'N/A',
  };
}

export function resolveSelectedInspectionDetails(
  items: readonly InspectionItem[],
  selection: InspectionSelection,
): InspectionItem | null {
  const { inspectionType, recordId } = selection;
  if (recordId != null) {
    const matchingRecord = items.find(
      (item) =>
        isAssetInspectionRecord(item) &&
        String(item.id) === String(recordId),
    );
    if (matchingRecord) {
      return matchingRecord;
    }
  }

  return findInspectionItemByDisplayTitle(items, inspectionType) ?? null;
}
